import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Tooltip, Typography } from 'antd';

import { ConfirmDeletingElement } from '@app/components';
import { BotDetailsModal, SelectTypeBotModal } from '@app/components/Modals';
import {
  EDIT_COMPONENT_TITLE_MAX_WIDTH,
  VIEW_COMPONENT_TITLE_MAX_WIDTH,
} from '@app/components/SalesFunnelColumnComponent/constants';
import { useSalesFunnelColumnComponent } from '@app/components/SalesFunnelColumnComponent/hooks';
import type { SalesFunnelColumnComponentType } from '@app/components/SalesFunnelColumnComponent/types';
import { PREFIX } from '@app/constants';
import { EditSalesFunnelRobotComponent } from '@app/pages/SalesFunnels/EditSalesFunnel/components/EditSalesFunnelRobotComponent';

import { ViewSalesFunnelChatComponent } from '@pages/SalesFunnels/ViewSalesFunnel/components/ViewSalesFunnelChatComponent';

import '@app/components/SalesFunnelColumnComponent/styles/SalesFunnelColumnComponent.desktop.scss';

const { Text } = Typography;

export const SalesFunnelColumnComponent: SalesFunnelColumnComponentType = ({
  data,
  isEditPageComponent,
  handleOpenSalesFunnelModal,
  setCurrentSalesFunnelStage,
  refetchGetSalesFunnelStages,
  robotsWithProgress,
}) => {
  const {
    isOpenDeleteModal,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
    itemToDelete,
    isOpenSelectTypeBotModal,
    openSelectTypeBotModal,
    closeSelectTypeBotModal,
    selectTypeBot,
    botTypeList,
    isLoadingBots,
    isOpenBotDetailsModal,
    closeBotDetailsModal,
    onSaveBotDetails,
    botDetailsModalTitle,
    triggerList,
    selectedBotTypeId,
    salesFunnelStagesList,
    servicesList,
    handleEditIconClick,
    handleAddIconClick,
    openEditBotHandler,
    isBotDetailsLoading,
    selectedBot,
    botDetailsModalType,
    employees,
    socialIntegrations,
    tags,
    getTagsHandler,
    allBranches,
    selectedBotFiles,
    isBotDetailsModalDataLoading,
    salesFunnelsList,
    getSalesFunnelStagesListHandler,
    isUploadingAttachments,
    taskUsersList,
    IsStageCreate,
    IsStageEdit,
    IsStageDelete,
  } = useSalesFunnelColumnComponent({
    stageFunnelData: data,
    refetchGetSalesFunnelStages,
    setCurrentSalesFunnelStage,
    handleOpenSalesFunnelModal,
  });

  return (
    <div className={`${PREFIX}sales-funnel-column-component`}>
      <Flex gap={5} className="main-flex">
        <div className="column-wrapper">
          <div
            className="status-line"
            style={{ backgroundColor: data.Color ? data.Color : '' }}
          />
          <div className="content-container">
            <div className="info-block">
              <div>
                <Flex justify="space-between" gap={5}>
                  <Flex gap={5}>
                    <Text
                      strong
                      className="sales-funnel-name"
                      style={{
                        maxWidth: isEditPageComponent
                          ? EDIT_COMPONENT_TITLE_MAX_WIDTH
                          : VIEW_COMPONENT_TITLE_MAX_WIDTH,
                      }}
                    >
                      {data.Name || ''}
                    </Text>
                    <Text strong>{`(${data.CountLeads || 0})`}</Text>
                  </Flex>
                  {isEditPageComponent ? (
                    <Flex gap={5}>
                      {IsStageEdit && (
                        <EditOutlined
                          className="action-icon"
                          onClick={handleEditIconClick}
                        />
                      )}
                      {IsStageDelete && (
                        <DeleteOutlined
                          className="action-icon"
                          onClick={() => openDeleteModal('stage')}
                        />
                      )}
                    </Flex>
                  ) : null}
                </Flex>
                <div className="description">{data.Description || ''}</div>
              </div>
              <Button icon={<PlusOutlined />} onClick={openSelectTypeBotModal}>
                Добавить
              </Button>
            </div>
            <Divider className="divider" />
            {!isEditPageComponent && data.Leads
              ? data.Leads.map((lead) => (
                  <ViewSalesFunnelChatComponent
                    key={lead.Id}
                    lead={lead}
                    stageId={data.Id}
                  />
                ))
              : null}
            {isEditPageComponent && data.Robots
              ? data.Robots.map((robot, index) => (
                  <EditSalesFunnelRobotComponent
                    key={`${robot.TriggerType}-${index}`}
                    robot={robot}
                    onClick={() => openEditBotHandler(robot)}
                    robotsWithProgress={robotsWithProgress}
                  />
                ))
              : null}
          </div>
        </div>

        {isEditPageComponent && IsStageCreate ? (
          <Tooltip title={'Добавить этап воронки продаж'}>
            <Button
              icon={<PlusOutlined />}
              className="add-intermediate-stage-button"
              onClick={handleAddIconClick}
            />
          </Tooltip>
        ) : null}
      </Flex>

      <ConfirmDeletingElement
        modalOpen={isOpenDeleteModal}
        setModalOpen={closeDeleteModal}
        handleDelete={handleDelete}
        content={
          itemToDelete === 'stage'
            ? 'Вы уверены, что хотите удалить этап воронки продаж?'
            : 'Вы уверены, что хотите удалить выбранного бота?'
        }
        title={
          itemToDelete === 'stage'
            ? 'Удаление этапа воронки продаж'
            : 'Удаление бота'
        }
      />

      <SelectTypeBotModal
        isOpen={isOpenSelectTypeBotModal}
        onCancel={closeSelectTypeBotModal}
        onSelect={selectTypeBot}
        list={botTypeList}
        isLoading={isLoadingBots}
        isButtonsLoading={isBotDetailsModalDataLoading}
      />

      <BotDetailsModal
        getSalesFunnelStagesListHandler={getSalesFunnelStagesListHandler}
        salesFunnelsList={salesFunnelsList}
        isOpen={isOpenBotDetailsModal}
        onCancel={closeBotDetailsModal}
        onConfirm={onSaveBotDetails}
        title={botDetailsModalTitle}
        triggerList={triggerList}
        selectedBotTypeId={selectedBotTypeId}
        salesFunnelStagesList={salesFunnelStagesList}
        servicesList={servicesList}
        isLoading={isBotDetailsLoading}
        onDeleteBot={() => openDeleteModal('bot')}
        bot={selectedBot}
        type={botDetailsModalType}
        employees={employees}
        socialIntegrations={socialIntegrations}
        tags={tags}
        getTagsHandler={getTagsHandler}
        allBranches={allBranches}
        selectedBotFiles={selectedBotFiles}
        isUploadingAttachments={isUploadingAttachments}
        taskUsersList={taskUsersList}
      />
    </div>
  );
};
